import { Transaction, Bill } from './types';

export interface ForecastDay {
  date: string; // YYYY-MM-DD
  projected: number; // running balance change
  cumulative: number; // cumulative from today
  label?: string; // e.g. bill name
}

export interface CashFlowForecast {
  days: ForecastDay[];
  totalIncome: number;
  totalExpenses: number;
  netChange: number;
}

/**
 * Build a 30-day cash flow forecast based on:
 * 1. Historical weekly income/expense averages
 * 2. Known upcoming bills
 */
export function buildForecast(
  transactions: Transaction[],
  bills: Bill[],
  daysAhead = 30
): CashFlowForecast {
  const now = new Date();
  const today = now.toISOString().slice(0, 10);

  // Calculate average daily income and expenses from last 60 days
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - 60);
  const cutoffStr = cutoff.toISOString().slice(0, 10);

  const recent = transactions.filter(t => t.date >= cutoffStr && t.date <= today);
  const totalDays = Math.max(
    Math.round((now.getTime() - cutoff.getTime()) / (1000 * 60 * 60 * 24)),
    1
  );

  const avgDailyIncome = recent
    .filter(t => t.type === 'income')
    .reduce((s, t) => s + t.amount, 0) / totalDays;

  const avgDailyExpense = recent
    .filter(t => t.type === 'expense')
    .reduce((s, t) => s + t.amount, 0) / totalDays;

  // Build day-by-day forecast
  const days: ForecastDay[] = [];
  let cumulative = 0;
  let totalIncome = 0;
  let totalExpenses = 0;

  // Index upcoming bills by date
  const billsByDate = new Map<string, { name: string; amount: number }[]>();
  for (const bill of bills.filter(b => !b.paid)) {
    const entries = billsByDate.get(bill.dueDate) ?? [];
    entries.push({ name: bill.name, amount: bill.amount });
    billsByDate.set(bill.dueDate, entries);
  }

  for (let i = 0; i < daysAhead; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i + 1);
    const dateStr = d.toISOString().slice(0, 10);

    let dayNet = avgDailyIncome - avgDailyExpense;
    let label: string | undefined;

    // Subtract known bills on their due date
    const dayBills = billsByDate.get(dateStr);
    if (dayBills) {
      for (const b of dayBills) {
        dayNet -= b.amount;
        label = (label ? label + ', ' : '') + b.name;
      }
    }

    cumulative += dayNet;
    totalIncome += avgDailyIncome;
    totalExpenses += avgDailyExpense + (dayBills?.reduce((s, b) => s + b.amount, 0) ?? 0);

    days.push({ date: dateStr, projected: dayNet, cumulative, label });
  }

  return {
    days,
    totalIncome: Math.round(totalIncome),
    totalExpenses: Math.round(totalExpenses),
    netChange: Math.round(totalIncome - totalExpenses),
  };
}
